import React, { memo, useMemo } from 'react'
import {
  OFFER_TYPE_TOUR_V2,
} from 'constants/offer'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import { buildSearchStringFromFilters } from 'lib/search/searchUtils'

import Heading from 'components/Luxkit/Typography/Heading'
import useOfferList from 'hooks/Offers/useOfferList'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { rem } from 'polished'
import styled from 'styled-components'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { useLereTourRecommendation } from 'hooks/Recommendation/useLereTourRecommendation'

const RecommendationsSection = styled(Pane)`
  padding: ${rem(40)} 0;
`

interface Props {
  offerId: string;
  trackingDescription: string;
  departureYear?: number;
  departureMonth?: number;
}

const MINIMUM_OFFERS_TO_DISPLAY = 4
const RECOMMENDATION_TYPES: Array<App.OfferParentType> = [
  OFFER_TYPE_TOUR_V2,
]

function TourV2YouMayAlsoLikeCarrousel(props: Props) {
  const {
    offerId,
    trackingDescription,
    departureYear,
    departureMonth,
  } = props

  // always fetch reco as we need that to decide whether user can be included in test
  const lereReco = useLereTourRecommendation(true, offerId, departureYear, departureMonth, ['direct_tour', 'partner_tour'])
  const lereLoading = !lereReco || lereReco.state === 'loading'
  const lereVersion = lereReco?.lereVersion
  const hasValidLereReco = (lereReco?.offers?.length ?? 0) >= MINIMUM_OFFERS_TO_DISPLAY
  const shouldUseLereReco = lereLoading || hasValidLereReco

  const filters = useMemo(() => {
    return {
      offerTypes: RECOMMENDATION_TYPES,
      offerIdsToExclude: [offerId],
    }
  }, [offerId])

  const defaultOfferList = useOfferList(filters)

  const offerList = useMemo(() => {
    if (shouldUseLereReco) {
      return {
        offerIds: lereReco?.offers?.map(offer => offer.offerId) ?? [],
        key: 'lereTourReco',
        fetching: lereLoading,
      }
    }
    return defaultOfferList
  }, [shouldUseLereReco, lereReco, lereLoading, defaultOfferList])

  const offerSource = useMemo(() => {
    return shouldUseLereReco ? `LERE - tours - ${lereVersion}` : 'Search - tours'
  }, [lereVersion, shouldUseLereReco])

  const tracking = useOfferListTracking(
    `Recommendations-Search Nearby-${trackingDescription}`,
    {
      ga: {
        category: 'Recommendations',
        label: `Search Nearby-${trackingDescription}`,
      },
      source: offerSource,
      key: JSON.stringify(offerId),
    },
  )

  const additionalQueryParams = useMemo(() => buildSearchStringFromFilters(filters), [filters])

  if (!offerList.fetching && offerList.offerIds.length < MINIMUM_OFFERS_TO_DISPLAY) {
    return null
  }

  return (
    <OfferListEventsProvider tracking={tracking}>
      <RecommendationsSection>
        <VerticalSpacer gap={16}>
          <LayoutContainer>
            <Heading variant="heading3">You may also like</Heading>
          </LayoutContainer>
          <OfferListCarousel
            overrideOfferList={offerList}
            additionalOfferUrlQuery={additionalQueryParams}
          />
        </VerticalSpacer>
      </RecommendationsSection>
    </OfferListEventsProvider>
  )
}

export default memo(TourV2YouMayAlsoLikeCarrousel)
